<div class="min-h-screen bg-cloud-mist font-sans">

  <main>
    <div class="tfr-page-shell py-8">
      <nav
        class="mb-4 flex items-center gap-2"
        aria-label="Migas de pan">
        <a
          routerLink="/explorar"
          class="inline-flex items-center gap-1 text-[13px] font-medium text-slate-blue transition-colors hover:text-midnight-indigo">
          <lucide-icon name="chevron-left" [size]="14" class="shrink-0" />
          Explorar espacios
        </a>
      </nav>

      @if (loading()) {
        <div class="flex min-h-[50vh] items-center justify-center">
          <svg class="h-8 w-8 animate-spin text-action-blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
        </div>
      }

      @if (!loading() && space()) {
        <div class="grid grid-cols-1 items-start gap-8 lg:grid-cols-3">

          <!-- Left: gallery, copy, map, terms, secure booking -->
          <div class="space-y-8 lg:col-span-2">

            <section>
              <div class="relative overflow-hidden rounded-2xl shadow-tfr-md sm:rounded-3xl">
                <div class="relative h-[220px] overflow-hidden rounded-2xl sm:h-[340px] sm:rounded-3xl lg:h-[460px]">
                  @if (galleryImages().length > 0) {
                    <img
                      [ngSrc]="galleryImages()[activeImageIndex()]"
                      [alt]="space()!.name"
                      fill
                      [priority]="true"
                      class="object-cover"
                      [sizes]="heroImageSizes" />
                  } @else {
                    <div class="flex h-full w-full items-center justify-center bg-gradient-to-br from-pale-gray to-platinum-tint">
                      <svg class="h-16 w-16 text-steel-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.4">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                    </div>
                  }

                  @if (galleryImages().length > 1) {
                    <button
                      type="button"
                      (click)="prevImage()"
                      class="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-midnight-indigo shadow-md transition hover:bg-white"
                      aria-label="Imagen anterior">
                      <lucide-icon name="chevron-left" [size]="18" />
                    </button>
                    <button
                      type="button"
                      (click)="nextImage()"
                      class="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-midnight-indigo shadow-md transition hover:bg-white"
                      aria-label="Imagen siguiente">
                      <lucide-icon name="chevron-right" [size]="18" />
                    </button>
                  }
                </div>
              </div>

              @if (gallery().length > 1) {
                <div class="mt-3 flex gap-2 overflow-x-auto pb-1">
                  @for (img of gallery().slice(0, 8); track $index) {
                    <button
                      type="button"
                      (click)="activeImageIndex.set($index)"
                      (mouseenter)="activeImageIndex.set($index)"
                      class="relative h-16 w-24 shrink-0 overflow-hidden rounded-xl border-2 transition-all"
                      [class]="activeImageIndex() === $index ? 'border-action-blue' : 'border-transparent hover:border-platinum-tint'">
                      <img
                        [src]="img.thumb"
                        [alt]="space()!.name + ' ' + ($index + 1)"
                        loading="lazy"
                        decoding="async"
                        class="h-full w-full object-cover" />
                    </button>
                  }
                </div>
              }
            </section>

            <div class="px-1">
              <h1 class="text-[30px] font-bold leading-tight text-midnight-indigo sm:text-[36px]">
                {{ space()!.name }}
              </h1>
              <p class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px] text-slate-blue">
                <span class="inline-flex items-center gap-1.5">
                  <lucide-icon name="map-pin" [size]="15" class="shrink-0 text-slate-blue" />
                  {{ locationLabel() }}
                </span>
                @if ((space()!.reviewCount ?? 0) > 0 && space()!.averageRating !== null) {
                  <span class="inline-flex items-center gap-1 font-semibold text-midnight-indigo">
                    <lucide-icon name="star" [size]="14" class="text-amber-500 fill-amber-500" />
                    <span class="tabular-nums">{{ space()!.averageRating | number:'1.1-1' }}</span>
                    <span class="font-normal text-steel-gray">({{ space()!.reviewCount }})</span>
                  </span>
                }
              </p>

              <p class="mt-5 text-[15px] leading-7 text-slate-blue">
                {{ space()!.description }}
              </p>
            </div>

            @if (showFeaturesSection()) {
              <div class="min-w-0 rounded-3xl border border-pale-gray bg-white px-4 py-5 sm:px-8 sm:py-6">
                <div class="mb-4 flex flex-wrap items-baseline justify-between gap-2">
                  <h2 class="text-[12px] font-semibold uppercase tracking-[0.16em] text-steel-gray">
                    Caracter&iacute;sticas del espacio
                  </h2>
                  @if (!featuresSectionLoading() && activeFeatureCount() > 0) {
                    <span class="text-[11px] font-medium tabular-nums text-steel-gray">
                      {{ activeFeatureCount() }} incluidas
                    </span>
                  }
                </div>

                @if (featuresSectionLoading()) {
                  <div class="space-y-4" aria-hidden="true">
                    @for (sk of [1, 2]; track sk) {
                      <div class="animate-pulse space-y-2.5">
                        <div class="h-3.5 w-28 rounded bg-pale-gray"></div>
                        <div class="flex flex-wrap gap-1.5">
                          <div class="h-7 w-24 rounded-full bg-cloud-mist"></div>
                          <div class="h-7 w-32 rounded-full bg-cloud-mist"></div>
                          <div class="h-7 w-20 rounded-full bg-cloud-mist"></div>
                        </div>
                      </div>
                    }
                  </div>
                } @else if (activeFeaturesGrouped().length > 0) {
                  <div class="space-y-5 sm:space-y-6">
                    @for (group of activeFeaturesGrouped(); track group.category.id) {
                      <section class="min-w-0">
                        <div class="mb-2.5 flex items-center gap-2">
                          <lucide-icon [name]="group.category.icon" class="h-3.5 w-3.5 shrink-0 text-steel-gray" />
                          <h3 class="text-[11px] font-semibold uppercase tracking-wider text-steel-gray">
                            {{ group.category.label }}
                          </h3>
                        </div>
                        <div class="flex flex-wrap gap-1.5 sm:gap-2">
                          @for (feat of group.features; track feat.id) {
                            <span
                              class="inline-flex max-w-full items-center gap-1.5 rounded-full border border-pale-gray bg-white py-1 pl-2 pr-2.5 text-[11px] font-medium leading-snug text-slate-blue shadow-[0_1px_2px_rgba(11,53,88,0.06)] sm:text-[12px] sm:py-1.5 sm:pl-2.5 sm:pr-3">
                              <lucide-icon [name]="feat.icon" class="h-3.5 w-3.5 shrink-0 text-steel-gray" />
                              <span class="min-w-0">{{ feat.label }}</span>
                            </span>
                          }
                        </div>
                      </section>
                    }
                  </div>
                } @else {
                  <p class="text-[13px] leading-relaxed text-steel-gray">
                    El propietario a&uacute;n no ha publicado el detalle del equipamiento de este espacio.
                  </p>
                }
              </div>
            }

            <div class="rounded-3xl border border-pale-gray bg-white px-6 py-6 sm:px-8">
              <h2 class="mb-4 text-[12px] font-semibold uppercase tracking-[0.16em] text-steel-gray">
                Localizaci&oacute;n
              </h2>

              @if (displayAddress()) {
                <p class="mb-4 flex items-start gap-2 text-[14px] leading-relaxed text-slate-blue">
                  <lucide-icon name="map-pin" [size]="16" class="mt-0.5 shrink-0 text-action-blue" />
                  <span>{{ displayAddress() }}</span>
                </p>
              } @else {
                <p class="mb-4 flex items-start gap-2 text-[14px] leading-relaxed text-slate-blue">
                  <lucide-icon name="map-pin" [size]="16" class="mt-0.5 shrink-0 text-action-blue" />
                  <span>{{ locationLabel() }}</span>
                </p>
              }

              @if (hasMap() && mapEmbedUrl(); as embedUrl) {
                <div class="aspect-[16/10] overflow-hidden rounded-2xl border border-pale-gray bg-cloud-mist">
                  <iframe
                    [src]="embedUrl"
                    class="block h-full w-full"
                    loading="lazy"
                    referrerpolicy="no-referrer-when-downgrade"
                    title="Ubicaci&oacute;n del espacio"
                    allowfullscreen></iframe>
                </div>
              }

              @if (mapSearchUrl()) {
                <a [href]="mapSearchUrl()" target="_blank" rel="noopener"
                   class="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-action-blue hover:underline">
                  <lucide-icon name="external-link" [size]="13" class="shrink-0" />
                  Abrir en Google Maps
                </a>
              }
            </div>

            <div class="rounded-3xl border border-pale-gray bg-white px-6 py-6 sm:px-8">
              <h2 class="mb-4 text-[12px] font-semibold uppercase tracking-[0.16em] text-steel-gray">
                Terminos y condiciones
              </h2>

              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div class="rounded-2xl bg-cloud-mist/60 p-4">
                  <p class="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-steel-gray">
                    <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white">
                      <lucide-icon name="calendar-clock" [size]="14" class="text-slate-blue" />
                    </span>
                    Cancelacion
                  </p>
                  <p class="text-[14px] font-semibold text-midnight-indigo">{{ cancellationSummary() }}</p>
                  <p class="mt-1 text-xs leading-relaxed text-steel-gray">{{ cancellationLateHint }}</p>
                </div>

                <div class="rounded-2xl bg-cloud-mist/60 p-4">
                  <p class="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-steel-gray">
                    <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white">
                      <lucide-icon name="shield" [size]="14" class="text-slate-blue" />
                    </span>
                    Fianza
                  </p>
                  <p class="text-[14px] font-semibold text-midnight-indigo">{{ depositSummary() }}</p>
                  <p class="mt-1 text-xs leading-relaxed text-steel-gray">{{ depositHint }}</p>
                </div>

                <div class="rounded-2xl bg-cloud-mist/60 p-4">
                  <p class="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-steel-gray">
                    <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white">
                      <lucide-icon name="brush" [size]="14" class="text-slate-blue" />
                    </span>
                    Limpieza
                  </p>
                  <p class="text-[14px] font-semibold text-midnight-indigo">{{ cleaningSummary }}</p>
                  <p class="mt-1 text-xs leading-relaxed text-steel-gray">{{ cleaningHint() }}</p>
                </div>

                <div class="rounded-2xl bg-cloud-mist/60 p-4">
                  <p class="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-steel-gray">
                    <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white">
                      <lucide-icon name="user" [size]="14" class="text-slate-blue" />
                    </span>
                    Edad minima
                  </p>
                  <p class="text-[14px] font-semibold text-midnight-indigo">{{ ageRequirementSummary() }}</p>
                </div>
              </div>

              @if (conditions().houseRules) {
                <div class="mt-4 border-t border-pale-gray pt-4">
                  <p class="mb-1 text-[11px] font-semibold uppercase tracking-wider text-steel-gray">
                    Normas del local
                  </p>
                  <p class="whitespace-pre-line text-[13px] leading-relaxed text-slate-blue">
                    {{ conditions().houseRules }}
                  </p>
                </div>
              }
            </div>

            <app-space-reviews-section
              [spaceId]="space()!.id!"
              [ownerId]="space()!.ownerId"
              [averageRating]="space()!.averageRating ?? null"
              [reviewCount]="space()!.reviewCount ?? 0" />

            <section class="rounded-3xl bg-white px-6 py-6 shadow-tfr-sm sm:px-8">
              <h2 class="mb-4 text-[12px] font-semibold uppercase tracking-[0.16em] text-steel-gray">
                Reserva segura
              </h2>
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div class="rounded-2xl bg-cloud-mist p-4">
                  <p class="mb-1 text-[11px] font-semibold uppercase tracking-wider text-steel-gray">Paso 1</p>
                  <p class="text-[13px] font-semibold text-midnight-indigo">{{ stepOneTitle() }}</p>
                  <p class="mt-1 text-[12px] leading-relaxed text-slate-blue">
                    {{ stepOneDescription() }}
                  </p>
                </div>
                <div class="rounded-2xl bg-cloud-mist p-4">
                  <p class="mb-1 text-[11px] font-semibold uppercase tracking-wider text-steel-gray">Paso 2</p>
                  <p class="text-[13px] font-semibold text-midnight-indigo">Formalizaci&oacute;n</p>
                  <p class="mt-1 text-[12px] leading-relaxed text-slate-blue">
                    {{ stepTwoDescription() }}
                  </p>
                </div>
                <div class="rounded-2xl bg-cloud-mist p-4">
                  <p class="mb-1 text-[11px] font-semibold uppercase tracking-wider text-steel-gray">Paso 3</p>
                  <p class="text-[13px] font-semibold text-midnight-indigo">Confirmaci&oacute;n del propietario</p>
                  <p class="mt-1 text-[12px] leading-relaxed text-slate-blue">
                    El propietario verifica el ingreso y confirma. Tu reserva queda firme y el contrato es legalmente vinculante.
                  </p>
                </div>
              </div>

              <p class="mt-4 flex items-start gap-2 text-[11px] leading-relaxed text-steel-gray">
                <lucide-icon name="info" [size]="12" class="mt-0.5 shrink-0" />
                <span>
                  Si no completas el Paso 2 dentro del plazo, la reserva se cancela autom&aacute;ticamente
                  y el hueco se libera. La tarifa de gesti&oacute;n del Paso 1 no es reembolsable.
                </span>
              </p>
            </section>
          </div>

          <!-- Right: sticky booking card (below fixed app-header + test banner) -->
          <aside
            class="lg:col-span-1 lg:sticky lg:z-10 lg:self-start lg:top-[calc(4rem+var(--tfr-banner-offset,0px)+1.75rem)]">
            <div class="rounded-3xl border border-pale-gray bg-white p-6 shadow-tfr-md">

              <div class="flex flex-wrap items-end gap-x-2 gap-y-1">
                <span class="text-[13px] text-steel-gray">Desde</span>
                <span class="text-[32px] font-bold leading-none tracking-tight text-midnight-indigo tabular-nums">
                  {{ formatPrice(fromHourlyRate()) }}
                </span>
                <span class="text-[13px] text-steel-gray">/ h</span>
              </div>
              <p class="mt-2 flex items-center gap-1.5 text-[12px] text-slate-blue">
                <lucide-icon name="clock-3" [size]="13" class="shrink-0 text-steel-gray" />
                M&iacute;nimo {{ minHours() }} h &middot;
                <span class="font-semibold text-midnight-indigo tabular-nums">{{ formatPrice(totalPrice()) }}</span>
              </p>

              @if (hasPaymentPreview()) {
                <div class="mt-5 border-t border-pale-gray pt-5">
                  <p class="mb-2 text-[11px] font-semibold uppercase tracking-wider text-steel-gray">
                    {{ paymentBreakdownHeading() }}
                  </p>
                  <div class="space-y-1.5 text-[12px]">
                    @if (isStripeCheckout()) {
                      <div class="flex items-center justify-between">
                        <span class="text-steel-gray">Alquiler (pago online)</span>
                        <span class="font-semibold tabular-nums text-midnight-indigo">{{ formatPrice(stripePayNowAmount()) }}</span>
                      </div>
                    } @else {
                      <div class="flex items-center justify-between">
                        <span class="text-steel-gray">{{ managementFeeLabel() }}</span>
                        <span class="font-semibold tabular-nums text-midnight-indigo">{{ formatPrice(managementFeeAmount()) }}</span>
                      </div>
                      <div class="flex items-center justify-between">
                        <span class="text-steel-gray">Se&ntilde;al al propietario</span>
                        <span class="font-semibold tabular-nums text-midnight-indigo">{{ formatPrice(ownerDirectAmount()) }}</span>
                      </div>
                      <div class="flex items-center justify-between border-t border-pale-gray pt-1.5">
                        <span class="text-steel-gray">Total alquiler</span>
                        <span class="font-semibold tabular-nums text-midnight-indigo">{{ formatPrice(totalPrice()) }}</span>
                      </div>
                    }
                  </div>
                  <p class="mt-2 flex items-start gap-2 text-[11px] leading-relaxed text-steel-gray">
                    <lucide-icon name="info" [size]="12" class="mt-0.5 shrink-0" />
                    <span>{{ paymentBreakdownDisclaimer() }}</span>
                  </p>
                </div>
              }

              <div class="mt-5 flex items-center gap-2 text-[13px] text-slate-blue">
                <lucide-icon name="users" [size]="15" class="shrink-0 text-steel-gray" />
                <span>Hasta {{ space()!.capacity }} personas</span>
              </div>

              <div class="mt-5 space-y-3">
                <button
                  type="button"
                  (click)="openCalendar()"
                  class="w-full rounded-xl bg-action-blue py-3.5 text-[15px] font-semibold text-white transition-all duration-200 hover:-translate-y-px hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-action-blue/40">
                  Comprobar disponibilidad
                </button>

                <button
                  type="button"
                  (click)="openShareModal()"
                  class="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-pale-gray bg-white py-3 text-[13px] font-semibold text-slate-blue transition-colors duration-150 hover:bg-cloud-mist hover:text-midnight-indigo focus:outline-none focus:ring-2 focus:ring-action-blue/20">
                  <lucide-icon name="share-2" [size]="16" class="shrink-0 text-action-blue" />
                  Compartir
                </button>
              </div>
            </div>
          </aside>
        </div>

        <div class="pb-20 lg:pb-0"></div>

        <!-- Mobile sticky bottom CTA -->
        <div
          class="fixed inset-x-0 bottom-0 z-30 border-t border-pale-gray bg-white/95 px-4 py-3 backdrop-blur lg:hidden"
          style="box-shadow: 0 -4px 16px rgba(11, 53, 88, 0.08);">
          <div class="tfr-page-shell flex items-center gap-3">
            <div class="min-w-0 flex-1">
              <p class="text-[10px] font-semibold uppercase leading-none tracking-[0.14em] text-steel-gray">
                Desde
              </p>
              <p class="mt-1 flex items-baseline gap-1 leading-none">
                <span class="text-[18px] font-bold tabular-nums text-midnight-indigo">{{ formatPrice(fromHourlyRate()) }}</span>
                <span class="text-[11px] text-steel-gray">/ h &middot; m&iacute;n {{ minHours() }} h</span>
              </p>
            </div>
            <button
              type="button"
              (click)="openCalendar()"
              class="shrink-0 rounded-xl bg-action-blue px-4 py-3 text-[13px] font-semibold text-white transition-all duration-200 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-action-blue/40">
              Comprobar disponibilidad
            </button>
          </div>
        </div>
      }
    </div>
  </main>
</div>

@if (calendarModalOpen() && space(); as calSpace) {
  <app-space-calendar-modal
    [space]="calSpace"
    (closed)="calendarModalOpen.set(false)" />
}

@if (shareModalOpen() && space(); as asset) {
  <div class="tfr-modal-overlay" (click)="closeShareModal()" role="dialog" aria-modal="true" aria-labelledby="share-space-detail-title">
    <div class="tfr-modal-panel max-w-md" (click)="$event.stopPropagation()">
      <div class="flex justify-center mb-4">
        <div class="h-12 w-12 rounded-2xl flex items-center justify-center bg-action-blue/10">
          <lucide-icon name="share-2" class="h-5 w-5 text-action-blue" />
        </div>
      </div>
      <h3 id="share-space-detail-title" class="text-[15px] font-bold text-center tracking-tight mb-1 text-midnight-indigo">
        Compartir espacio
      </h3>
      <p class="text-[12px] text-center leading-relaxed mb-5 text-slate-blue px-1">
        Enlace p&uacute;blico de <strong class="text-midnight-indigo">{{ asset.name }}</strong>
        @if (!asset.active) {
          <span class="block mt-1 text-steel-gray">El espacio est&aacute; oculto: solo ser&aacute; visible cuando lo publiques.</span>
        }
      </p>

      <label class="sr-only" for="share-space-detail-url">Enlace del espacio</label>
      <div class="flex gap-2 mb-4">
        <input
          id="share-space-detail-url"
          type="text"
          readonly
          [value]="getShareUrl(asset)"
          class="min-w-0 flex-1 rounded-xl border border-pale-gray bg-cloud-mist/60 px-3 py-2.5 text-[11px] text-midnight-indigo truncate focus:outline-none focus:ring-2 focus:ring-action-blue/30" />
        <button type="button" (click)="copyShareLink()"
          class="shrink-0 rounded-xl border border-pale-gray bg-snow-white px-3 py-2.5 text-[11px] font-semibold text-midnight-indigo transition-colors hover:bg-cloud-mist focus:outline-none focus:ring-2 focus:ring-action-blue/30">
          @if (linkCopied()) { Copiado } @else { Copiar }
        </button>
      </div>

      <div class="flex flex-col gap-2">
        @if (canNativeShare) {
          <button type="button" (click)="nativeShare()"
            class="flex w-full items-center justify-center gap-2 rounded-xl border border-pale-gray bg-snow-white px-4 py-3 text-[13px] font-semibold text-midnight-indigo transition-colors hover:bg-cloud-mist focus:outline-none focus:ring-2 focus:ring-action-blue/30">
            <lucide-icon name="share-2" class="h-4 w-4 shrink-0 text-action-blue" />
            M&aacute;s opciones&hellip;
          </button>
          <p class="text-[10px] text-center text-steel-gray -mt-0.5 mb-1">
            Abre el men&uacute; del sistema (WhatsApp, correo, etc.)
          </p>
        }
        <button type="button" (click)="shareOnWhatsApp()"
          class="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-[13px] font-semibold text-white transition-colors bg-[#25D366] hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-[#25D366]/40">
          <svg class="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp
        </button>
        <button type="button" (click)="closeShareModal()" class="tfr-btn-secondary w-full mt-1">
          Cerrar
        </button>
      </div>
    </div>
  </div>
}
